import _ from 'underscore';

import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Helpers } from '../../ts/helpers/Helpers';
import { GroupKeys, IGroup } from '../../ts/ServerApi/types/IGroup';
import { IUser, UserKeys } from '../../ts/ServerApi/types/IUser';
import { IAuthorizableGranteePermissions } from '../requests/types/IAuthorizableGranteePermissions';
import { CapabilityValue, ICapabilityValue } from '../requests/types/ICapabilityValue';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { IPermission, Permission, PermissionReason } from '../types/Permission';

export module PermissionsMerger {

  /**
   * Converts a list of IAuthorizableGranteePermissions from the server into an equivalent list of
   * IGranteePermissions for use by the client. The input array can contain AGPs for multiple
   * authorizables and multiple users, but their client-side authorizable type must be the same.
   */
  export function makeGranteePermissionsList(
    agps: IAuthorizableGranteePermissions[],
    authorizableIds: string[],
    groups: IGroup[],
    users: IUser[]
  ): IGranteePermissions[]
  {
    const groupDict = Helpers.dictFromArray<IGroup>(groups, GroupKeys.id);
    const userDict = Helpers.dictFromArray<IUser>(users, UserKeys.id);
    const agpListByGrantee = _.groupBy(agps,
      (agp: IAuthorizableGranteePermissions) => agp.grantee.type + agp.grantee.id);
    let permissionsList: IGranteePermissions[] = _.map(agpListByGrantee,
      (granteeAgpList: IAuthorizableGranteePermissions[]) => {
        return createGranteePermissions(
          granteeAgpList,
          authorizableIds,
          groupDict,
          userDict);
      });

    // All Users group used to be hardcoded into the list when there are no explicit permissions set, 
    // but this special treatment should be removed when collectionsPermissionsEnabled is on.
    if (!FeatureFlags.collectionsPermissionsEnabled) {
      const allUsersGroup = _.findWhere(groups, {isAllUsersGroup: true});
      if (allUsersGroup) {
        // there exists an All Users group
        let allUsersGroupGP = _.find(permissionsList,
          (gp: IGranteePermissions) => (gp.group && gp.group.id === allUsersGroup.id));
        if (!allUsersGroupGP) {
          // the All Users group doesn't have any explicit permissions,
          // add it to the permissions list with unspecified permissions:
          allUsersGroupGP = {group: allUsersGroup, permissionsByContentType: {} };
          permissionsList.push(allUsersGroupGP);
        }
      }
    }

    // system user should not show up in permissions grid
    const systemUser = _.findWhere(users, { username: '_system' });
    if (systemUser) {
      const systemUserGP = _.find(permissionsList, (gp: IGranteePermissions) => (gp.user && gp.user.id === systemUser.id));
      permissionsList = _.without(permissionsList, systemUserGP);
    }

    return permissionsList;
  }

  function createGranteePermissions(
    granteeAgpList: IAuthorizableGranteePermissions[],
    authorizableIds: string[],
    groupDict: _.Dictionary<IGroup>,
    userDict: _.Dictionary<IUser>
  ): IGranteePermissions
  {
    // we know all grantees in this list are the same because of the groupBy
    const grantee = granteeAgpList[0].grantee;

    const granteePermissions: IGranteePermissions = {
      permissionsByContentType: mergePermissionListByContentType(granteeAgpList, authorizableIds)
    };

    switch (grantee.type) {
      case 'group':
        granteePermissions.group = groupDict[grantee.id];
        break;
      case 'user':
        granteePermissions.user = userDict[grantee.id];
        break;
    }
    return granteePermissions;
  }

  /**
   * Merges a list of IAuthorizableGranteePermissions to a single dictionary that maps from
   * capability to permission. Ignores the grantee (assumes all the items in the input list are
   * for a single grantee).
   */
  function mergePermissionListByContentType(
    granteeAgpList: IAuthorizableGranteePermissions[],
    authorizableIds: string[]
  ): _.Dictionary<_.Dictionary<IPermission>>
  {
    // The permissions for each authorizable type will end up as a separate section
    // in the IGranteePermissions, so we process each individually.
    const agpsByContentType = _.groupBy(granteeAgpList,
      (agp: IAuthorizableGranteePermissions) => agp.authorizable.type);

    const permissionsByContentType: _.Dictionary<_.Dictionary<IPermission>> = {};

    _.each(agpsByContentType,
      (agps: IAuthorizableGranteePermissions[], contentType: string) => {
        const permissions = aggregatePermissionsForAuthorizables(agps, authorizableIds);
        permissionsByContentType[contentType] = permissions;
      }
    );

    return permissionsByContentType;
  }

  function aggregatePermissionsForAuthorizables(
    agpsForAuthorizable: IAuthorizableGranteePermissions[],
    authorizableIds: string[]
  ): _.Dictionary<IPermission>
  {
    // There may not be an agp for every authorizable we care about, so we have to merge the
    // permissions by authorizable ID, rather than by agp.
    const agpByAuthorizable = _.indexBy(agpsForAuthorizable,
      (agp: IAuthorizableGranteePermissions) => agp.authorizable.id);

    let aggregatedPermissions: _.Dictionary<IPermission> = null;

    authorizableIds.forEach((authorizableId: string) => {
      const agp = agpByAuthorizable[authorizableId];
      const inputPermissions: _.Dictionary<IPermission> =
        agp ? permissionsFromCapabilityValues(agp.permissions) : {};
      if (aggregatedPermissions) {
        mergePermissions(aggregatedPermissions, inputPermissions);
      } else {
        aggregatedPermissions = inputPermissions;
      }
    });

    return aggregatedPermissions;
  }

  function permissionsFromCapabilityValues(
    capabilityValues: ICapabilityValue[]
  ): _.Dictionary<IPermission>
  {
    const permissions: _.Dictionary<IPermission> = {};
    _.each(capabilityValues, (capabilityValue: ICapabilityValue) => {
      const capability = capabilityValue.capability;
      const permission = permissionFromCapabilityValue(capabilityValue);
      const reason = reasonFromCapabilityValue(capabilityValue);
      if (permissions[capability]) {
        permissions[capability].permission = permission;
        permissions[capability].reason = reason;
      } else {
        permissions[capability] = {permission: permission, reason: reason};
      }
    });
    return permissions;
  }

  function permissionFromCapabilityValue(capability: ICapabilityValue): Permission {
    const capabilityValue = capability.value;
    switch (capabilityValue) {
      case CapabilityValue.Allowed: return Permission.Allowed;
      case CapabilityValue.Denied: return Permission.Denied;
      // A single capability value from the server is never "mixed".
      default: return Permission.Unspecified;
    }
  }

  function reasonFromCapabilityValue(capability: ICapabilityValue): PermissionReason {
    const capabilityReason = capability.reason;
    switch (capabilityReason) {
      case 'GROUP_ALLOW':             return PermissionReason.GroupAllow;
      case 'GROUP_DISALLOW':          return PermissionReason.GroupDeny;
      case 'GROUP_PROJECT_LEADER':    return PermissionReason.GroupProjectLeaderAllow;
      case 'GUEST_ALLOWED':           return PermissionReason.GuestAllow;
      case 'DERIVED_ALLOW':           return PermissionReason.DerivedAllow;
      case 'DERIVED_WRITE_ALLOW':     return PermissionReason.DerivedWriteAllow;
      case 'IMPLICIT_DENY':           return PermissionReason.ImplicitDeny;
      case 'USER_ADMIN':              return PermissionReason.UserAdminAllow;
      case 'USER_ALLOW':              return PermissionReason.UserAllow;
      case 'USER_AUTHORIZABLE_OWNER': return PermissionReason.UserAuthorizableOwnerAllow;
      case 'USER_DISALLOW':           return PermissionReason.UserDeny;
      case 'USER_LICENSING':          return PermissionReason.UserLicensingDeny;
      case 'USER_PROJECT_LEADER':     return PermissionReason.UserProjectLeaderAllow;
      case 'USER_PROJECT_OWNER':      return PermissionReason.UserProjectOwnerAllow;
      default:                        return PermissionReason.Unspecified;
    }
  }

  function mergePermissions(
    current: _.Dictionary<IPermission>,
    input: _.Dictionary<IPermission>
  ): void
  {
    _.each(_.union(_.keys(current), _.keys(input)), (capability: string) => {

      // some vars just to improve readability
      const curr = current[capability];
      const inpt = input[capability];

      if (curr && inpt) {
        if (curr.permission !== inpt.permission) {
          // the values explicitly differ
          current[capability].permission  = Permission.Mixed;
          current[capability].reason      = PermissionReason.Mixed;
        } else {
          // the values are explicitly equal
          if (curr.reason !== inpt.reason) {
            // the reasons explicitly differ
            current[capability].reason      = PermissionReason.Mixed;
          }
        }

      } else if (curr){ // && !inpt
        if (curr.permission !== Permission.Unspecified) {
          // input is implicitly unspecified, current is explicitly specified
          current[capability].permission  = Permission.Mixed;
          current[capability].reason      = PermissionReason.Mixed;
        }

      } else if (inpt) { // && !curr
        if (inpt.permission !== Permission.Unspecified) {
          // input is explicitly specified, current is implicitly unspecified
          current[capability] = { permission: Permission.Mixed, reason: PermissionReason.Mixed };
        } else {
          // input is explicitly unspecified, current is implicitly unspecified
          current[capability] = { permission: inpt.permission, reason: inpt.reason };
        }
      }

    });
  }
}
